import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import LocalOfferOutlinedIcon from '@material-ui/icons/LocalOfferOutlined';
import SettingsCard from 'components/kea/SettingsCard/SettingsCard';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import { useContextualPricingStatus } from 'pluginAdmin/features/contextualPricing/useContextualPricingStatus';
import { useShellAppContext } from '../shell/context/useShellAppContext';
import DataSettingsToggle from './DataSettingsToggle';

const useStyles = makeStyles((theme) => ({
  card: { marginTop: theme.spacing(3) },
  layout: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
  },
  identity: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    minWidth: 0,
  },
  icon: {
    display: 'flex',
    width: 40,
    height: 40,
    flex: '0 0 auto',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    background: 'var(--kea-color-primary-soft)',
    color: 'var(--kea-color-primary)',
  },
  copy: { maxWidth: 650 },
  description: { marginTop: theme.spacing(0.5) },
  control: {
    display: 'flex',
    minHeight: 44,
    flex: '0 0 auto',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  controlLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    fontWeight: 600,
  },
  error: { marginTop: theme.spacing(2) },
}));

export default function ContextualPricingSection() {
  const classes = useStyles();
  const t = useLocalizedMessage();
  const { compatibility, bridgeClient } = useShellAppContext();
  const supported = compatibility?.contextualPricing?.managed === true;
  const {
    api, state, setState, isLoading, error, setError, reload,
  } = useContextualPricingStatus({ enabled: supported, bridgeClient });
  const [isSaving, setIsSaving] = useState(false);

  if (!supported) return null;

  const handleToggle = async (event) => {
    const enabled = event.target.checked;
    if (!state || isLoading || isSaving || error) return;
    setIsSaving(true);
    setError(null);
    try {
      setState(await api.setEnabled(enabled));
    } catch (nextError) {
      setError(nextError);
    } finally {
      setIsSaving(false);
    }
  };

  const enabled = state?.enabled === true;
  const disabled = isLoading || isSaving || !state || !!error;
  let statusKey = null;
  if (isLoading) statusKey = 'contextualPricing.dataSettings.status.loading';
  else if (!state && !error) statusKey = 'contextualPricing.dataSettings.status.unavailable';

  return (
    <SettingsCard className={classes.card} data-testid="contextual-pricing-card">
      <Box className={classes.layout}>
        <Box className={classes.identity}>
          <Box className={classes.icon} aria-hidden="true">
            {isLoading ? <CircularProgress size={20} color="inherit" /> : <LocalOfferOutlinedIcon />}
          </Box>
          <Box className={classes.copy}>
            <Typography variant="h6">{t('contextualPricing.dataSettings.title')}</Typography>
            <Typography variant="body2" color="textSecondary" className={classes.description}>
              {t('contextualPricing.dataSettings.description')}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.control}>
          {statusKey && (
            <span className={classes.controlLabel} id="contextual-pricing-toggle-status" aria-live="polite">
              {t(statusKey)}
            </span>
          )}
          <DataSettingsToggle
            checked={enabled}
            onChange={handleToggle}
            disabled={disabled}
            label={t('contextualPricing.dataSettings.toggle')}
            describedBy={statusKey ? 'contextual-pricing-toggle-status' : undefined}
            testId="contextual-pricing-toggle"
          />
        </Box>
      </Box>
      {error && (
        <Alert
          severity="error"
          className={classes.error}
          action={(
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setError(null);
                reload();
              }}
            >
              {t('contextualPricing.dataSettings.retry')}
            </Button>
          )}
        >
          {t('contextualPricing.dataSettings.error')}
        </Alert>
      )}
    </SettingsCard>
  );
}
